import { CircularProgress, styled } from '@mui/material'
import { VersionReportVersionEntity } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import moment from 'moment'
import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded'
import {
  LoadingContainer,
  TableContainer,
  TableHeader,
  TableBody,
  TableRow,
  HeaderCell,
  DataCell,
  NameCell,
  NameText,
  ReportRowIcon,
  DialogContent,
  SelectMenuHeader,
  BreadcrumbListIcon,
  BreadcrumbFolderIcon,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
  AddButton,
} from './commons'
import { StyledDivider } from '../commons'
import { RenameMenu } from './RenameMenu'
import { memo, useCallback, useState } from 'react'
import { intl } from '../../../../../i18n'

interface ReportMenuProps {
  templateName: string
  reports: VersionReportVersionEntity[]
  loading: boolean
  onReportSelect: (report: VersionReportVersionEntity) => void
  onReportUpdate: (report: VersionReportVersionEntity) => void
  onReportDelete: (report: VersionReportVersionEntity) => void
  onBackToTemplate: () => void
  onCreateNewReport: () => void
  onClose: () => void
}

export const ReportMenu = ({
  templateName,
  reports,
  loading,
  onReportSelect,
  onReportUpdate,
  onReportDelete,
  onBackToTemplate,
  onCreateNewReport,
  onClose,
}: ReportMenuProps) => {
  const [openMenuUuid, setOpenMenuUuid] = useState<string | null>(null)
  const handleMenuOpenChange = useCallback((uuid: string, isOpen: boolean) => {
    setOpenMenuUuid(isOpen ? uuid : null)
  }, [])

  return (
    <>
      <SelectMenuHeader
        breadcrumb={
          <>
            <BreadcrumbListIcon />
            <BreadcrumbLink onClick={onBackToTemplate}>
              フォルダ一覧
            </BreadcrumbLink>
            <BreadcrumbSeparator />
            <BreadcrumbFolderIcon />
            <BreadcrumbCurrent>{templateName}</BreadcrumbCurrent>
          </>
        }
        actions={
          <AddButton
            onClick={onCreateNewReport}
            tooltip="報告書を作成"
            icon={<NoteAddRoundedIcon sx={{ fontSize: 18 }} />}
          />
        }
        onClose={onClose}
      />
      <StyledDivider />
      <DialogContent>
        {loading ? (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        ) : (
          <TableContainer>
            <TableHeader>
              <HeaderCell width="52%">名称</HeaderCell>
              <HeaderCell width="22%">更新者</HeaderCell>
              <HeaderCell width="20%">更新日時</HeaderCell>
              <HeaderCell width="6%"></HeaderCell>
            </TableHeader>
            <TableBody>
              {reports.map(report => (
                <ReportRow
                  key={report.uuid}
                  report={report}
                  isMenuOpen={openMenuUuid === report.uuid}
                  onMenuOpenChange={handleMenuOpenChange}
                  onReportSelect={onReportSelect}
                  onReportUpdate={onReportUpdate}
                  onReportDelete={onReportDelete}
                />
              ))}
            </TableBody>
          </TableContainer>
        )}
      </DialogContent>
    </>
  )
}

const ReportRow = memo(
  ({
    report,
    isMenuOpen,
    onMenuOpenChange,
    onReportSelect,
    onReportUpdate,
    onReportDelete,
  }: {
    report: VersionReportVersionEntity
    isMenuOpen: boolean
    onMenuOpenChange: (uuid: string, isOpen: boolean) => void
    onReportSelect: (report: VersionReportVersionEntity) => void
    onReportUpdate: (report: VersionReportVersionEntity) => void
    onReportDelete: (report: VersionReportVersionEntity) => void
  }) => {
    const handleSelect = useCallback(() => {
      onReportSelect(report)
    }, [report, onReportSelect])

    const handleRename = useCallback(() => {
      onReportUpdate(report)
    }, [report, onReportUpdate])

    const handleDelete = useCallback(() => {
      onReportDelete(report)
    }, [report, onReportDelete])

    const handleMenuOpenChange = useCallback(
      (isOpen: boolean) => {
        onMenuOpenChange(report.uuid, isOpen)
      },
      [report.uuid, onMenuOpenChange]
    )

    return (
      <ReportTableRow>
        <NameCell width="52%" onClick={handleSelect}>
          <ReportRowIcon />
          <NameText>{report.name}</NameText>
        </NameCell>
        <DataCell width="22%" onClick={handleSelect}>
          {report.updatedBy.name}
        </DataCell>
        <DataCell width="20%" onClick={handleSelect}>
          {moment(report.updatedAt).format('MM/DD HH:mm')}
        </DataCell>
        <ActionCell width="6%">
          <MenuButtonWrapper className="report-action-menu">
            <RenameMenu
              isOpen={isMenuOpen}
              onOpenChange={handleMenuOpenChange}
              onRename={handleRename}
              label={intl.formatMessage({
                id: 'versionReport.rename.menuLabel',
              })}
              onDelete={handleDelete}
              deleteLabel={intl.formatMessage({
                id: 'versionReport.delete.menuLabel',
              })}
            />
          </MenuButtonWrapper>
        </ActionCell>
      </ReportTableRow>
    )
  }
)

ReportRow.displayName = 'ReportRow'

const ReportTableRow = styled(TableRow)({
  '&:hover .report-action-menu': {
    opacity: 1,
  },
})

const ActionCell = styled('div')<{ width: string }>(({ width }) => ({
  width,
  padding: '12px 8px',
  fontSize: '13px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
}))

const MenuButtonWrapper = styled('div')({
  opacity: 0,
  transition: 'opacity 0.2s',
})
